/**
 * LOGOI LAB - Main Application Component
 * 
 * Auth: Supabase Auth (Google, Apple, Guest)
 * Sessions persist via localStorage (survives tab close)
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LogosHumano from './components/LogosHumano';
import IFCModel from './components/IFCModel';
import { supabase, signInWithGoogle, signInWithApple, signOut } from './services/supabase';
import { Capacitor } from '@capacitor/core';
import { getApiBaseUrl } from './utils/api-base';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard } from '@capacitor/keyboard';
import './App.css';
import './styles/kaira-integration.css';

export default function App() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsPhone, setNeedsPhone] = useState(false);
  const [phoneInput, setPhoneInput] = useState('+52 ');
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [showWhatsAppOptIn, setShowWhatsAppOptIn] = useState(false);

  // Initialize Capacitor native plugins
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
      SplashScreen.hide().catch(() => {});
      Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => {});

      // Listen for OAuth deep link callback
      CapApp.addListener('appUrlOpen', async ({ url }) => {
        if (url.includes('login-callback')) {
          // Extract tokens from the URL fragment (#access_token=...&refresh_token=...)
          const hashPart = url.split('#')[1];
          if (hashPart) {
            const params = new URLSearchParams(hashPart);
            const accessToken = params.get('access_token');
            const refreshToken = params.get('refresh_token');
            if (accessToken && refreshToken) {
              await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
              });
            }
          }
          // Close the in-app browser
          Browser.close().catch(() => {});
        }
      });
    }
  }, []);

  // Listen for Supabase auth state changes
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      if (s?.user) {
        checkProfilePhone(s.user);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, s) => {
        setSession(s);
        if (s?.user) {
          checkProfilePhone(s.user);
        } else {
          setUser(null);
          setNeedsPhone(false);
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Check if user has phone + whatsapp_optin in profile
  async function checkProfilePhone(authUser) {
    const { data } = await supabase
      .from('profiles')
      .select('phone, whatsapp_optin')
      .eq('id', authUser.id)
      .single();

    // Sync to M5 PostgreSQL to get integer user ID (for cross-channel)
    let dbUserId = null;
    try {
      const meta = authUser.user_metadata || {};
      const syncRes = await fetch(`${getApiBaseUrl()}/api/auth/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          google_id: authUser.id,
          email: authUser.email,
          name: meta.full_name || meta.name || authUser.email,
          picture: meta.avatar_url || meta.picture,
        }),
      });
      if (syncRes.ok) {
        const syncData = await syncRes.json();
        dbUserId = syncData.user?.id || null;
        if (dbUserId) sessionStorage.setItem('logos-db-user-id', String(dbUserId));
        console.log('[APP] M5 sync OK, dbUserId:', dbUserId);
      }
    } catch (e) {
      console.warn('[APP] M5 sync error:', e.message);
    }

    if (data?.phone) {
      setUser({ ...mapSupabaseUser(authUser), phone: data.phone, dbUserId });
      setNeedsPhone(false);
      // Show WhatsApp opt-in if they haven't seen it yet
      if (!data.whatsapp_optin) setShowWhatsAppOptIn(true);
    } else {
      setUser({ ...mapSupabaseUser(authUser), dbUserId });
      setNeedsPhone(true);
    }
    setLoading(false);
  }

  // Save phone to profiles + register on M5 + send WhatsApp welcome
  async function handlePhoneSave() {
    const cleaned = phoneInput.replace(/[\s\-\(\)]/g, '');
    if (!/^\+\d{10,15}$/.test(cleaned)) {
      setPhoneError(t('phone.errorInvalid'));
      return;
    }

    setPhoneSaving(true);
    setPhoneError('');

    // Save phone to Supabase profiles
    const { error } = await supabase
      .from('profiles')
      .update({ phone: cleaned })
      .eq('id', user.id);

    if (error) {
      setPhoneError(t('phone.errorSaving', { message: error.message }));
      setPhoneSaving(false);
      return;
    }

    // Register phone on M5 (links channel_users + sends WhatsApp welcome)
    const dbUserId = user.dbUserId || sessionStorage.getItem('logos-db-user-id');
    if (dbUserId) {
      try {
        const regRes = await fetch(`${getApiBaseUrl()}/api/phone/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: parseInt(dbUserId),
            phone: cleaned,
            name: user.name,
            email: user.email,
          }),
        });
        const regData = await regRes.json();
        console.log('[APP] Phone registered on M5:', regData);
      } catch (e) {
        console.warn('[APP] M5 phone register error:', e.message);
      }
    }

    setUser(prev => ({ ...prev, phone: cleaned }));
    setNeedsPhone(false);
    setShowWhatsAppOptIn(true);
    setPhoneSaving(false);
  }

  // Handle WhatsApp opt-in
  async function handleWhatsAppOptIn(accepted) {
    if (user && !user.isGuest) {
      await supabase
        .from('profiles')
        .update({ whatsapp_optin: true })
        .eq('id', user.id);
    }
    if (accepted && user?.phone) {
      // Open WhatsApp with pre-filled message
      const msg = encodeURIComponent(t('whatsapp.welcomeMessage'));
      window.open(`https://wa.me/19788012275?text=${msg}`, '_blank');
    }
    setShowWhatsAppOptIn(false);
  }

  const handleLogout = async () => {
    await signOut();
    setUser(null);
    setSession(null);
    setNeedsPhone(false);
  };

  const handleGuestLogin = () => {
    setUser({
      id: `guest_${Date.now()}`,
      name: t('app.guestName'),
      email: 'invitado@logoilab.com',
      picture: '/logo.svg',
      isGuest: true,
    });
  };

  if (loading) {
    return (
      <div className="login-container">
        <div className="login-content">
          <div className="logo-container">
            <div className="logo">
              <img src="/logo.svg" alt="LOGOS" />
            </div>
          </div>
          <p className="subtitle">{t('app.loading')}</p>
        </div>
      </div>
    );
  }

  // ═══ WHATSAPP OPT-IN SCREEN ═══
  if (user && !user.isGuest && !needsPhone && showWhatsAppOptIn) {
    return (
      <div className="login-container">
        <div className="login-content">
          <div className="logo-container">
            <div className="logo">
              <img src="/logo.svg" alt="LOGOS" />
            </div>
          </div>
          
          <h1>{t('whatsapp.title')}</h1>
          <p className="subtitle">
            {t('whatsapp.subtitle', { name: user.name })}
          </p>
          
          <div className="whatsapp-optin-card">
            <div className="whatsapp-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <p className="whatsapp-desc">
              {t('whatsapp.description')}
            </p>
            <p className="whatsapp-desc" style={{fontSize: '13px', opacity: 0.6}}>
              {t('whatsapp.syncNote')}
            </p>
          </div>
          
          <button
            onClick={() => handleWhatsAppOptIn(true)}
            className="whatsapp-btn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            {t('whatsapp.connect')}
          </button>
          
          <button
            onClick={() => handleWhatsAppOptIn(false)}
            className="guest-btn"
            style={{ marginTop: 12 }}
          >
            {t('whatsapp.skip')}
          </button>
        </div>
      </div>
    );
  }

  // ═══ PHONE COLLECTION SCREEN (after OAuth, before app access) ═══
  if (user && !user.isGuest && needsPhone) {
    return (
      <div className="login-container">
        <div className="login-content">
          <div className="logo-container">
            <div className="logo">
              <img src="/logo.svg" alt="LOGOS" />
            </div>
          </div>
          
          <h1>{t('phone.title')}</h1>
          <p className="subtitle">{t('phone.subtitle', { name: user.name })}</p>
          
          <div className="profile-info-row">
            <span className="profile-label">{t('phone.emailLabel')}</span>
            <span className="profile-value">{user.email}</span>
          </div>
          
          <div className="phone-form">
            <label className="phone-label">{t('phone.label')}</label>
            <input
              type="tel"
              value={phoneInput}
              onChange={e => setPhoneInput(e.target.value)}
              placeholder={t('phone.placeholder')}
              className="phone-input"
              autoFocus
            />
            <p className="phone-hint">{t('phone.hint')}</p>
            {phoneError && <p className="phone-error">{phoneError}</p>}
            
            <button
              onClick={handlePhoneSave}
              disabled={phoneSaving}
              className="google-btn"
              style={{ marginTop: 16 }}
            >
              {phoneSaving ? t('phone.saving') : t('phone.continue')}
            </button>
          </div>
          
          <button onClick={handleLogout} className="guest-btn" style={{ marginTop: 16 }}>
            {t('app.logout')}
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="login-container">
        <div className="login-content">
          <div className="logo-container">
            <div className="logo">
              <img src="/logo.svg" alt="LOGOS" />
            </div>
          </div>
          
          <h1>{t('app.title')}</h1>
          <p className="subtitle">{t('app.subtitle')}</p>
          
          <div className="login-buttons">
            <button 
              onClick={() => signInWithGoogle()}
              className="google-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('app.signInGoogle')}
            </button>
            
            <button 
              onClick={() => signInWithApple()}
              className="apple-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              {t('app.signInApple')}
            </button>

            <button 
              onClick={handleGuestLogin}
              className="guest-btn"
            >
              {t('app.guestLogin')}
            </button>
          </div>
          
          <p className="login-note">
            {t('app.loginNote')}
          </p>
          
          <div className="features">
            <h3>{t('app.explore')}</h3>
            <div className="feature-list">
              <span className="feature-tag">{t('features.friston')}</span>
              <span className="feature-tag">{t('features.levin')}</span>
              <span className="feature-tag">{t('features.watson')}</span>
              <span className="feature-tag">{t('features.hoffman')}</span>
              <span className="feature-tag">{t('features.penrose')}</span>
              <span className="feature-tag">{t('features.domains')}</span>
              <span className="feature-tag">{t('features.dimensions')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Access token for Google Calendar API (from Supabase OAuth session)
  const accessToken = session?.provider_token || null;

  return (
    <BrowserRouter>
      <div className="app">
        <header className="app-header">
          <div className="user-info">
            <img src={user.picture} alt={user.name} className="user-avatar" />
            <span>{t('app.welcome', { name: user.name })}</span>
            <button onClick={handleLogout} className="logout-btn">
              {t('app.logout')}
            </button>
          </div>
        </header>
        <Routes>
          <Route path="/" element={<LogosHumano user={user} accessToken={accessToken} onLogout={handleLogout} />} />
          <Route path="/logos" element={<LogosHumano user={user} accessToken={accessToken} onLogout={handleLogout} />} />
          <Route path="/ifc" element={<IFCModel />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

/**
 * Map Supabase auth user to the format expected by LOGOS components.
 */
function mapSupabaseUser(authUser) {
  const meta = authUser.user_metadata || {};
  return {
    id: authUser.id,                                    // Supabase UUID
    email: authUser.email,
    name: meta.full_name || meta.name || authUser.email,
    picture: meta.avatar_url || meta.picture || '/logo.svg',
    createdAt: authUser.created_at || null,
    isGuest: false,
  };
}
